import {Component, OnDestroy, OnInit} from '@angular/core';
import {AlertService, GroupService, UserService} from "../services";
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

import {Router, ActivatedRoute} from '@angular/router';
import {first, takeUntil} from 'rxjs/operators';
import {CustomerSourceService} from "../services/customerSource.service";
import {ReplaySubject} from 'rxjs';

import {locale as polish} from './i18n/pl';
import {locale as english} from './i18n/en';
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";
import {ComponentTitleService} from "../services/componentTitle.service";
import {Country} from "../models/country";
import {CountryService} from "../services/country.service";
import {VoucherTemplate} from "../models/voucherTemplate";
import {VoucherTemplateService} from "../services/voucherTemplate.service";
import {User} from "../models";
import {CustomerSource} from "../models/customerSource";
import {Items} from "../models/items";
import {MatDialog} from "@angular/material/dialog";
import {CustomerAlreadyExistsDialogComponent} from "./dialog/customerAlreadyExistsDialog.component";
import {CardService} from "../services/card.service";
import {Subject} from "rxjs";
import {InternalFileService} from "../internalFile/service/internalFile.service";
import {File} from "../models/file";

@Component({
    templateUrl: './customerForm.component.html',
    styleUrls: ['./customerForm.component.scss']
})

export class CustomerFormComponent implements OnInit, OnDestroy {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    customerSources: any[];
    countries: Country[];
    filteredCountries: ReplaySubject<Country[]> = new ReplaySubject<Country[]>(1);
    countryFilterCtrl: FormControl = new FormControl();
    filteredCompanyCountries: ReplaySubject<Country[]> = new ReplaySubject<Country[]>(1);
    companyCountryFilterCtrl: FormControl = new FormControl();
    documents: any[] = [];
    vouchers: VoucherTemplate[] = [];
    dialogRef: any;
    user: User;
    cardExists = false;
    cardNumber: string = '';
    checkingCardNumber = false;
    private _unsubscribeAll: Subject<any>;
    downloading = false;
    birthDays = [
        "01", "02", "03", "04", "05", "06", "07", "08", "09",
        "10", "11", "12", "13", "14", "15", "16", "17", "18",
        "19", "20", "21", "22", "23", "24", "25", "26", "27",
        "28", "29", "30", "31",
    ];

    birthMonths = [
        {
            "id": "01",
            "name": "January"
        },
        {
            "id": "02",
            "name": "February"
        },
        {
            "id": "03",
            "name": "March"
        },
        {
            "id": "04",
            "name": "April"
        },
        {
            "id": "05",
            "name": "May"
        },
        {
            "id": "06",
            "name": "June"
        },
        {
            "id": "07",
            "name": "July"
        },
        {
            "id": "08",
            "name": "August"
        },
        {
            "id": "09",
            "name": "September"
        },
        {
            "id": "10",
            "name": "October"
        },
        {
            "id": "11",
            "name": "November"
        },
        {
            "id": "12",
            "name": "December"
        },
    ];
    selectedFile: any = null;

    birthYears = [];

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private alertService: AlertService,
        private customerSourceService: CustomerSourceService,
        private translationLoader: FuseTranslationLoaderService,
        private componentTitleService: ComponentTitleService,
        private countryService: CountryService,
        private voucherTemplateService: VoucherTemplateService,
        private cardService: CardService,
        private internalFileService: InternalFileService,
        public _matDialog: MatDialog,
    ) {
        for (let i = 2021; i >= 1940; i--) {
            this.birthYears.push(i);
        }

        this.translationLoader.loadTranslations(polish, english);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Customers');
    }

    ngOnInit() {
        this._unsubscribeAll = new Subject();
        this.id = this.route.snapshot.params['id'];
        this.isAddMode = !this.id;

        this.countryService.getAll().subscribe((res: any[]) => {
            this.countries = res;

            // Initialize filtered countries
            this.filteredCountries.next(this.countries.slice());
            this.filteredCompanyCountries.next(this.countries.slice());

            // Listen for country search field value changes
            this.countryFilterCtrl.valueChanges
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.filterCountries();
                });

            // Listen for company country search field value changes
            this.companyCountryFilterCtrl.valueChanges
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.filterCompanyCountries();
                });

            // Set Poland as default country for new customers
            if (this.isAddMode && this.countries && this.countries.length > 0) {
                const poland = this.countries.find(c => c.name === 'Poland' || c.iso2 === 'PL');
                if (poland) {
                    this.f.country.setValue(poland.id);
                }
            }
        }, error => {
            this.alertService.error(error);
        })

        this.voucherTemplateService.getOpenDayVouchers().pipe(first())
            .subscribe((res: VoucherTemplate[]) => {
                    this.vouchers = res;
                },
                error => {
                    this.alertService.error(error);
                })

        this.customerSourceService.getAll().pipe(first())
            .subscribe((res: CustomerSource[]) => {
                    this.customerSources = res;
                },
                error => {
                    this.alertService.error(error);
                });

        // password not required in edit mode
        const passwordValidators = [Validators.minLength(6)];
        if (this.isAddMode) {
            passwordValidators.push(Validators.required);

            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', Validators.required],
            street: [''],
            houseNumber: [''],
            flatNumber: [''],
            postCode: [''],
            city: [''],
            country: [''],
            phone: [''],
            sex: [''],
            birthdayDay: ['', Validators.required],
            birthdayMonth: ['', Validators.required],
            birthdayYear: ['', Validators.required],
            clientSource: ['', Validators.required],
            nip: [''],
            companyName: [''],
            companyStreet: [''],
            companyCity: [''],
            companyPostCode: [''],
            companyCountry: [''],
            readTheRegulations: [false],
            readThePriceList: [false],
            readTheContraindications: [false],
            consentToMarketing: [false],
            gdprAgrees: [false],
            cardNumber: [''],
            openDayVoucherId: [''],
            multiSport: [''],
            medicover: [''],
            medicoverPlus: [false],
            profit: [false],
            pzuSport: [false],
            canAddFirstTimeVoucher: [false],
            description: [],
        });

        if (!this.isAddMode) {
            this.user = this.route.snapshot.data.userData;

            let birthday = new Date(this.user.birthday);
            let nameDay = new Date(this.user.nameDay);
            let month = String(birthday.getMonth() + 1);

            let day = String(birthday.getDate());

            if (month.length == 1) {
                month = "0" + month;
            }

            if (day.length == 1) {
                day = "0" + day;
            }

            this.f.firstName.setValue(this.user.firstName);
            this.f.lastName.setValue(this.user.lastName);
            this.f.email.setValue(this.user.email);
            this.f.phone.setValue(this.user.phone);
            this.f.street.setValue(this.user.street);
            this.f.houseNumber.setValue(this.user.houseNumber);
            this.f.flatNumber.setValue(this.user.flatNumber);
            this.f.postCode.setValue(this.user.postCode);
            this.f.city.setValue(this.user.city);
            if (this.user.country) {
                this.f.country.setValue(this.user.country.id);
            }
            this.f.sex.setValue(this.user.sex);
            this.f.birthdayDay.setValue(day);
            this.f.birthdayMonth.setValue(month);
            this.f.birthdayYear.setValue(birthday.getFullYear());
            if (this.user.clientSource) {
                this.f.clientSource.setValue(this.user.clientSource.id);
            }
            this.f.nip.setValue(this.user.nip);
            this.f.companyName.setValue(this.user.companyName);
            this.f.companyStreet.setValue(this.user.companyStreet);
            this.f.companyCity.setValue(this.user.companyCity);
            this.f.companyPostCode.setValue(this.user.companyPostCode);
            if (this.user.companyCountry) {
                this.f.companyCountry.setValue(this.user.companyCountry.id);
            }
            this.f.gdprAgrees.setValue(this.user.gdprAgrees);
            this.f.consentToMarketing.setValue(this.user.consentToMarketing);
            this.f.readTheContraindications.setValue(this.user.readTheContraindications);
            this.f.readThePriceList.setValue(this.user.readThePriceList);
            this.f.readTheRegulations.setValue(this.user.readTheRegulations);
            this.f.multiSport.setValue(this.user.multiSport);
            this.f.medicover.setValue(this.user.medicover);
            this.f.medicoverPlus.setValue(this.user.medicoverPlus);
            this.f.pzuSport.setValue(this.user.pzuSport);
            this.f.profit.setValue(this.user.profit);
            this.f.canAddFirstTimeVoucher.setValue(this.user.canAddFirstTimeVoucher);
            this.f.description.setValue(this.user.description);
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    onFileSelected(event: any): void {
        this.selectedFile = event.target.files[0] ?? null;
    }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // Manual validation for birthday fields (empty string is treated as valid by Validators.required for mat-select)
        const birthdayDay = this.f.birthdayDay.value;
        const birthdayMonth = this.f.birthdayMonth.value;
        const birthdayYear = this.f.birthdayYear.value;

        if (!birthdayDay || birthdayDay === '' || !birthdayMonth || birthdayMonth === '' || !birthdayYear || birthdayYear === '') {
            this.f.birthdayDay.setErrors({ required: true });
            this.f.birthdayMonth.setErrors({ required: true });
            this.f.birthdayYear.setErrors({ required: true });

            this.submitted = false;
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createUser();
        } else {
            this.updateUser();
        }
    }

    private createUser() {
        let customer = this.form.value;

        if (!customer.openDayVoucherId || customer.openDayVoucherId.length == 0) {
            customer.canAddFirstTimeVoucher = true;
        }

        const formData = new FormData();
        Object.keys(customer).forEach(key => {
            if (!customer[key]) {
                customer[key] = '';
            }

            formData.append(key, customer[key]);
        });

        formData.append('birthday', customer['birthdayYear'] + "-" + customer['birthdayMonth'] + "-" + customer['birthdayDay']);

        if (this.documents) {
            for (let i = 0; i < this.documents.length; i++) {
                formData.append("file[]", this.documents[i]);
            }
        }

        this.userService.createCustomerFromForm(formData, customer)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('User added successfully', {keepAfterRouteChange: true});
                    this.submitted = false;
                    this.router.navigate(['.', {relativeTo: this.route}]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                    this.submitted = false;
                });
    }

    private updateUser() {
        let customer = this.form.value;

        const formData = new FormData();
        Object.keys(customer).forEach(key => {
            if (!customer[key]) {
                customer[key] = '';
            }

            formData.append(key, customer[key]);
        });

        for (let i = 0; i < this.documents.length; i++) {
            formData.append("file[]", this.documents[i]);
        }

        this.userService.updateCustomer(this.id, formData, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Update successful', {keepAfterRouteChange: true});
                    this.submitted = false;
                    this.router.navigate(['customers/details/' + this.user.id]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                    this.submitted = false;
                });
    }

    onDocumentsChange(event) {
        this.documents = [];
        if (event.target.files && event.target.files.length) {
            this.documents = event.target.files ?? null;
        }
    }

    checkIfCustomerExistsByName() {
        const customer = this.form.value;

        if (customer.firstName.length > 3 && customer.lastName.length > 3) {
            this.userService.findCustomerByFirstNameAndLastName(customer.firstName, customer.lastName).pipe(first())
                .subscribe((res: Items) => {
                        if (res.items.length > 0) {
                            this.dialogRef = this._matDialog.open(CustomerAlreadyExistsDialogComponent, {
                                panelClass: 'customer-already-exists-dialog',
                                data: {
                                    action: 'show',
                                    reason: 'names'
                                },
                                width: '700px',
                            });

                            this.dialogRef.disableClose = true;
                            this.dialogRef.backdropClick().subscribe(_ => {
                            });
                        }
                    },
                    error => {

                    });
        }
    }

    verifyCardNumber(event: any) {
        this.cardNumber = event.target.value;
        this.checkingCardNumber = true;
        this.cardExists = true;
        this.cardService.getByNumber(event.target.value).pipe(takeUntil(this._unsubscribeAll))
            .subscribe(data => {
                if ((!this.user && data.userId && data.userId.length > 0) || (data.userId !== this.user.id)) {
                    this.cardExists = true;
                } else {
                    this.cardExists = false;
                }
                this.checkingCardNumber = false;
            }, error => {
                this.cardExists = false;
                this.checkingCardNumber = false;
            });
    }

    downloadFile(document: File) {
        document.downloading = true;
        this.internalFileService.downloadFile(document.id).subscribe(res => {
            var byteCharacters = atob(res);
            var byteNumbers = new Array(byteCharacters.length);
            for (var i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            var byteArray = new Uint8Array(byteNumbers);
            var file = new Blob([byteArray], { type: document.mimeType + ';base64' });
            var fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            document.downloading = false;
        }, err => {
            document.downloading = false;
        });
    }

    private filterCountries() {
        if (!this.countries) {
            return;
        }
        // get the search keyword
        let search = this.countryFilterCtrl.value;
        if (!search) {
            this.filteredCountries.next(this.countries.slice());
            return;
        } else {
            search = search.toLowerCase();
        }
        // filter the countries
        this.filteredCountries.next(
            this.countries.filter(country => country.name.toLowerCase().indexOf(search) > -1)
        );
    }

    private filterCompanyCountries() {
        if (!this.countries) {
            return;
        }
        // get the search keyword
        let search = this.companyCountryFilterCtrl.value;
        if (!search) {
            this.filteredCompanyCountries.next(this.countries.slice());
            return;
        } else {
            search = search.toLowerCase();
        }
        // filter the countries
        this.filteredCompanyCountries.next(
            this.countries.filter(country => country.name.toLowerCase().indexOf(search) > -1)
        );
    }

    private unsubscribe(): void {
        this._unsubscribeAll.next();
    }
}
